import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RN_TO_WEBVIEW_EVENTS, WEBVIEW_EVENTS } from '../../constant/constant';
import { sendPostMessage } from '../../utils/postMessage';

// A revealed parked page counts as stale once it sat hidden longer than this.
const STALE_AGE_MS = 30_000;
// Floor between two shown-triggered refetches (rapid park/unpark cycles must
// not turn into an invalidate storm).
const REFETCH_MIN_INTERVAL_MS = 15_000;

/**
 * Bridge routing for preloaded/parked WebView instances. Renders nothing.
 *
 * The native shell keeps WebView instances alive and re-shows them instead of
 * doing a fresh document load. Two things must then happen page-side:
 *
 * 1. RN_TO_WEBVIEW_EVENTS.NAVIGATE ('kc-navigate', payload
 *    `data: { path, search?, nonce }`): client-side-navigate the SPA to the
 *    requested route with `replace` (the previously-shown route must not sit in
 *    the history stack — hardware back should behave as if the WebView was
 *    opened directly on the requested route). Once the router actually shows
 *    that route we ack with WEBVIEW_EVENTS.NAVIGATE_ACK (`{ nonce, path }`).
 *    No ack (invalid path, route never applied, old web build) ⇒ the native
 *    side times out and falls back to a full document load, so either deploy
 *    order is safe.
 *
 * 2. The 'kc-webview-shown' `window` CustomEvent (detail
 *    `{ preloaded, age_ms }`), fired when the host reveals an already-loaded
 *    instance: if the page sat parked long enough to be stale, refetch what is
 *    on screen via a React Query invalidate.
 *
 * Mount once inside the Router (App.js). Inert outside the app WebView: these
 * events simply never fire in a normal browser, and every handler is guarded so
 * bridge handling can never break the page.
 */
export default function BridgeNavigation() {
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();

	// Latest navigate in a ref so the mount-once message listener never has to
	// re-subscribe (same pattern as useHardwareBack's handlerRef).
	const navigateRef = useRef(navigate);
	navigateRef.current = navigate;

	// The one navigation we've applied but not yet observed on the router:
	// `{ nonce, path, href }`. The ack is sent from the location effect below,
	// so it truthfully means "route is applied", not just "navigate() was
	// called".
	const pendingAckRef = useRef(null);

	useEffect(() => {
		const onMessage = (event) => {
			try {
				let payload = event?.data;
				// iOS/string transport safety — most platforms deliver an object.
				if (typeof payload === 'string') {
					try {
						payload = JSON.parse(payload);
					} catch {
						return;
					}
				}
				if (payload?.type !== RN_TO_WEBVIEW_EVENTS.NAVIGATE) return;

				const { path, search, nonce } = payload?.data || {};
				// Same-app absolute paths only: reject non-strings, relative paths,
				// full URLs ('https://…' has no leading '/') and protocol-relative
				// hosts ('//evil.example').
				if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) return;

				const href = path + (typeof search === 'string' ? search : '');
				pendingAckRef.current = { nonce, path, href };
				navigateRef.current(href, { replace: true });
			} catch {
				// Bridge handling must never break the page.
			}
		};

		window.addEventListener('message', onMessage);
		// Android RN WebView dispatches the injected MessageEvent on `document`;
		// it bubbles to `window`, but listen on both to be transport-agnostic
		// (mirrors useHardwareBack). A double delivery is harmless here: it
		// re-applies the same replace-navigation, and the nonce match below
		// still acks exactly once.
		document.addEventListener('message', onMessage);
		return () => {
			window.removeEventListener('message', onMessage);
			document.removeEventListener('message', onMessage);
		};
	}, []);

	// Ack once the router actually shows the requested route. A location change
	// that does not match keeps the request pending: our navigation may still be
	// in flight behind an unrelated commit, and if it truly got superseded the
	// native side's ack timeout falls back to a full document load anyway (a
	// late ack for an expired nonce is ignored native-side).
	useEffect(() => {
		const pending = pendingAckRef.current;
		if (!pending) return;
		if (location.pathname + location.search + location.hash !== pending.href) return;
		pendingAckRef.current = null;
		sendPostMessage(WEBVIEW_EVENTS.NAVIGATE_ACK, { nonce: pending.nonce, path: pending.path });
	}, [location]);

	// Refetch-on-show: when the host reveals a stale parked instance, refresh
	// whatever React Query data is on screen.
	const lastInvalidateAtRef = useRef(0);
	useEffect(() => {
		const onShown = (event) => {
			try {
				const ageMs = Number(event?.detail?.age_ms);
				// NaN fails this comparison too — only refetch when the page really
				// sat parked long enough to be stale.
				if (!(ageMs > STALE_AGE_MS)) return;
				const now = Date.now();
				if (now - lastInvalidateAtRef.current < REFETCH_MIN_INTERVAL_MS) return;
				lastInvalidateAtRef.current = now;
				queryClient.invalidateQueries();
			} catch {
				// Refresh is best-effort — never let it break the page.
			}
		};

		window.addEventListener(RN_TO_WEBVIEW_EVENTS.WEBVIEW_SHOWN, onShown);
		return () => window.removeEventListener(RN_TO_WEBVIEW_EVENTS.WEBVIEW_SHOWN, onShown);
	}, [queryClient]);

	return null;
}
